<template>
  <v-container fluid class="customer-list-page pa-0">
    <Create
      :createDialog="createDialog"
      @close="createDialog = false"
      @submit="getList"
    />

    <Edit
      :editDialog="editDialog"
      @close="editDialog = false"
      @submit="getList"
      :editCustomer="selectedCustomer"
    />

    <v-card :loading="loading" outlined class="customer-list-card">
      <div class="customer-list-header">
        <div>
          <div class="page-title">Customer List</div>
          <div class="page-subtitle">
            {{ customers.length }} customer{{ customers.length === 1 ? "" : "s" }} found
          </div>
        </div>

        <v-btn
          v-if="checkRightStatus(7)"
          small
          rounded
          color="primary"
          dark
          class="new-customer-button"
          @click="add"
        >
          <v-icon left small>mdi-plus</v-icon>
          <span class="new-customer-label">New Customer</span>
        </v-btn>
      </div>

      <div class="customer-filter-bar">
        <v-text-field
          v-model="search"
          outlined
          prepend-inner-icon="search"
          autofocus
          dense
          label="Business, contact, phone, address"
          single-line
          hide-details
          clearable
          class="customer-search"
        />

        <v-btn
          v-if="$vuetify.breakpoint.xsOnly"
          icon
          class="customer-filter-trigger"
          @click="mobileFilterSheet = true"
        >
          <v-icon>mdi-tune-variant</v-icon>
        </v-btn>
      </div>

      <v-bottom-sheet v-model="mobileFilterSheet" inset max-width="520">
        <v-card class="mobile-filter-card">
          <div class="mobile-filter-header">
            <v-btn icon @click="mobileFilterSheet = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <div class="mobile-filter-title">Filters</div>
          </div>

          <v-card-text>
            <v-select
              v-model="itemsPerPage"
              :items="[10, 20, 50, 100]"
              label="Rows per page"
              outlined
              dense
              hide-details
              @change="page = 1"
            />

            <v-btn
              block
              color="primary"
              rounded
              depressed
              class="mt-4"
              @click="mobileFilterSheet = false"
            >
              Apply
            </v-btn>
          </v-card-text>
        </v-card>
      </v-bottom-sheet>

      <v-card-text class="pa-4">
        <div class="summary-row mb-4">
          <div class="summary-box">
            <div class="summary-label">List Total</div>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div class="summary-value" v-bind="attrs" v-on="on">
                  {{ compactCurrency(total) }}
                </div>
              </template>
              <span>{{ fullCurrency(total) }}</span>
            </v-tooltip>
          </div>

          <div class="summary-box">
            <div class="summary-label">Result Total</div>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div class="summary-value" v-bind="attrs" v-on="on">
                  {{ compactCurrency(fullTotal) }}
                </div>
              </template>
              <span>{{ fullCurrency(fullTotal) }}</span>
            </v-tooltip>
          </div>
        </div>

        <v-data-table
          :mobile-breakpoint="0"
          :headers="headers"
          :items="customers"
          :search="search"
          :loading="loading"
          :sort-desc="sortDesc"
          @current-items="updateTotal"
          :sort-by="sortBy"
          :page.sync="page"
          :items-per-page="itemsPerPage"
          :hide-default-footer="$vuetify.breakpoint.xsOnly"
          :footer-props="{ 'items-per-page-options': [10, 15, 25, 50, 100] }"
          class="customer-table"
        >
          <template v-slot:item="{ item }">
            <tr
              class="pointer customer-row"
              :class="{ 'blocked-customer-row': item.blockOrder }"
            >
              <td class="text-left" @click="viewProfile(item)">
                <div
                  class="font-weight-bold"
                  v-html="$highlight(item.name || '', safeSearch)"
                >
                  {{ item.name }}
                </div>
                <div
                  v-if="!$vuetify.breakpoint.xsOnly"
                  class="caption grey--text"
                >
                  {{ item.email || "" }}
                </div>
              </td>

              <td
                v-if="!$vuetify.breakpoint.xsOnly"
                class="text-left"
                @click="viewProfile(item)"
                v-html="$highlight(item.contactName || '', safeSearch)"
              >
                {{ item.contactName }}
              </td>

              <td class="text-left" @click="viewProfile(item)">
                <div
                  v-if="item.cellNumber || item.phoneNumber"
                  v-html="
                    $highlight(
                      item.cellNumber || item.phoneNumber || '',
                      safeSearch
                    )
                  "
                >
                  {{ item.cellNumber || item.phoneNumber }}
                </div>
                <div v-else class="grey--text">Not Set</div>
              </td>

              <td
                v-if="!$vuetify.breakpoint.xsOnly"
                class="text-left"
                @click="viewProfile(item)"
              >
                <div
                  v-if="item.address"
                  v-html="$highlight(item.address || '', safeSearch)"
                >
                  {{ item.address }}
                </div>
                <div class="caption grey--text">
                  {{ item.city || "" }}
                  <span v-if="item.city && item.state">,</span>
                  {{ item.state || "" }}
                </div>
              </td>

              <td
                v-if="!$vuetify.breakpoint.xsOnly"
                class="text-right font-weight-bold"
                @click="viewProfile(item)"
              >
                <span v-if="checkRightStatus(33)">
                  {{ item.balance | currency }}
                </span>
              </td>

              <td
                v-if="!$vuetify.breakpoint.xsOnly"
                class="text-right"
                @click="viewProfile(item)"
              >
                <span v-if="checkRightStatus(33)">
                  {{ item.balanceLimit | currency }}
                </span>
              </td>

              <td
                v-if="!$vuetify.breakpoint.xsOnly"
                class="text-right"
                @click="viewProfile(item)"
              >
                <span
                  v-if="checkRightStatus(33)"
                  :class="balanceDiff(item) < 0 ? 'negative-value' : 'positive-value'"
                >
                  {{ balanceDiff(item) | currency }}
                </span>
              </td>

              <td
                v-if="!$vuetify.breakpoint.xsOnly"
                class="justify-end align-center text-right"
              >
                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon small>
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list dense class="menu-button">
                    <v-list-item @click="viewProfile(item)">
                      <v-list-item-title>Profile</v-list-item-title>
                    </v-list-item>

                    <v-divider />

                    <v-list-item
                      v-if="checkRightStatus(23)"
                      @click="editCustomer(item)"
                    >
                      <v-list-item-title>
                        {{ $t("buttons.edit") }}
                      </v-list-item-title>
                    </v-list-item>

                    <DeleteCustomer :Customer="item" @submit="getList()" />
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>

          <v-alert slot="no-results" :value="true" color="error" icon="warning">
            Your search for "{{ search }}" found no results.
          </v-alert>

        </v-data-table>

        <v-pagination
          v-if="$vuetify.breakpoint.xsOnly && mobilePageCount > 1"
          v-model="page"
          :length="mobilePageCount"
          :total-visible="5"
          circle
          class="mobile-pagination mt-3"
        />
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import customerService from "../service.js";
import DeleteCustomer from "../components/DeleteCustomer";

const Create = () => import("./Create");
const Edit = () => import("./Edit");

export default {
  name: "customer-list",

  components: {
    Create,
    Edit,
    DeleteCustomer,
  },

  data() {
    return {
      createDialog: false,
      editDialog: false,
      selectedCustomer: null,
      mobileFilterSheet: false,
      total: 0,
      fullTotal: 0,
      desktopHeaders: [
        {
          text: "Business Name",
          value: "name",
          sortable: true,
          align: "left",
        },
        {
          text: "Contact Name",
          value: "contactName",
          align: "left",
          sortable: true,
        },
        {
          text: "Cell Number",
          value: "cellNumber",
          align: "left",
          sortable: true,
        },
        {
          text: "Address",
          value: "address",
          align: "left",
          sortable: true,
        },
        {
          text: this.$t("labels.balance"),
          value: "balance",
          align: "right",
          sortable: true,
        },
        {
          text: "Limit",
          value: "balanceLimit",
          align: "right",
          sortable: true,
        },
        {
          text: "Available",
          value: "diff",
          align: "right",
          sortable: false,
        },
        {
          text: "",
          value: "action",
          sortable: false,
        },
      ],
      page: 1,
      sortBy: "balance",
      pageCount: 0,
      sortDesc: true,
      itemsPerPage: 10,
      search: "",
      customers: [],
      loading: true,
    };
  },

  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),

    headers() {
      if (this.$vuetify.breakpoint.xsOnly) {
        return [
          { text: "Business Name", value: "name", sortable: true },
          {
            text: "Phone Number",
            value: "cellNumber",
            sortable: true,
            align: "left",
          },
        ];
      }
      return this.desktopHeaders;
    },

    safeSearch() {
      return (this.search || "").toLowerCase();
    },
    mobilePageCount() {
      return Math.max(1, Math.ceil(this.customers.length / this.itemsPerPage));
    },
  },

  created() {
    this.getList();
    this.$events.listen("customerListEvent", () => this.getList());
  },

  beforeDestroy() {
    this.$events.remove("customerListEvent");
  },

  methods: {
    compactCurrency(value) {
      const amount = Number(value || 0);
      const absolute = Math.abs(amount);
      let divisor = 1;
      let suffix = "";

      if (absolute >= 1000000) {
        divisor = 1000000;
        suffix = "M";
      } else if (absolute >= 1000) {
        divisor = 1000;
        suffix = "K";
      }

      return `$${(amount / divisor).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: suffix ? 1 : 2,
      })}${suffix}`;
    },
    fullCurrency(value) {
      return Number(value || 0).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });
    },
    balanceDiff(customer) {
      return Number(customer.balanceLimit || 0) - Number(customer.balance || 0);
    },

    updateTotal(val) {
      this.total = val.reduce((sum, row) => {
        return sum + Number(row.balance || 0);
      }, 0);
    },

    viewProfile(customer) {
      this.$router.push({ path: `/customer/${customer.id}/profile` });
    },

    add() {
      this.createDialog = true;
    },

    async getList() {
      this.loading = true;
      this.editDialog = false;
      this.createDialog = false;

      try {
        const where = {};

        if (!this.currentUser.isAdmin && this.checkRightStatus(50)) {
          where.SalesRepUserId = this.currentUser.id;
        }

        const response = await customerService.getAll(where);

        this.customers = response.data || [];

        this.fullTotal = this.customers.reduce((sum, row) => {
          return sum + Number(row.balance || 0);
        }, 0);

        return response;
      } catch (error) {
        console.error("customer getList failed", error);
        this.customers = [];
        this.total = 0;
        this.fullTotal = 0;
      } finally {
        this.loading = false;
      }
    },

    editCustomer(customer) {
      this.selectedCustomer = customer;
      this.editDialog = true;
    },
  },
};
</script>

<style scoped>
.customer-list-page {
  background: #f8fafc;
}

.customer-list-card {
  border-radius: 20px;
  background: #ffffff;
}

.customer-list-header {
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
}

.page-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.customer-filter-bar {
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.customer-search {
  max-width: 420px;
  min-width: 280px;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 220px));
  gap: 12px;
}

.summary-box {
  background: #f8fafc;
  border-radius: 16px;
  padding: 14px;
}

.summary-label {
  font-size: 12px;
  color: #64748b;
}

.summary-value {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin-top: 4px;
}

.customer-table {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}

.customer-row:hover {
  background: #f8fafc;
}

.blocked-customer-row,
.blocked-customer-row:hover {
  background: #fff1f2 !important;
}

.positive-value {
  color: #16a34a;
  font-weight: 700;
}

.negative-value {
  color: #dc2626;
  font-weight: 700;
}

.mobile-filter-card {
  border-radius: 20px 20px 0 0;
}

.mobile-filter-header {
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.mobile-filter-title {
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
}

@media (max-width: 600px) {
  .customer-list-header {
    flex-wrap: nowrap;
  }

  .new-customer-button {
    width: 40px !important;
    min-width: 40px !important;
    max-width: 40px !important;
    height: 40px !important;
    min-height: 40px !important;
    max-height: 40px !important;
    aspect-ratio: 1 / 1;
    padding: 0 !important;
    border-radius: 50% !important;
    flex: 0 0 40px;
    margin-left: auto;
    align-items: center;
    justify-content: center;
  }

  .new-customer-button .v-icon {
    margin: 0 !important;
  }

  .new-customer-label {
    display: none;
  }

  .customer-search {
    max-width: none;
    min-width: 0;
    flex: 1;
  }

  .customer-filter-bar {
    flex-wrap: nowrap;
  }

  .summary-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .summary-box {
    padding: 12px;
  }

  .summary-value {
    font-size: 20px;
  }

  .mobile-pagination {
    display: flex;
    justify-content: center;
  }

  .mobile-pagination::v-deep .v-pagination__item,
  .mobile-pagination::v-deep .v-pagination__navigation {
    background: #ffffff !important;
    border: 1px solid #cbd5e1;
    box-shadow: none;
  }

  .mobile-pagination::v-deep .v-pagination__item--active {
    color: var(--v-primary-base) !important;
    border-color: var(--v-primary-base);
    font-weight: 700;
  }
}
</style>
